import React, { ChangeEvent, useContext, useState } from "react";
import cookie from "react-cookies";
import { Controller, useForm } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";

import { Input } from "@airhelp/react";
import { Box, Button, Heading, Link, VStack } from "@chakra-ui/react";

import { authApi } from "api";
import config from "config/config";
import { USER_RETURN_AHPLUS_COOKIE } from "config/cookies";
import { PAGE_PATHS } from "config/routes";
import { SessionContext } from "contexts";
import { ErrorBadge } from "elements";
import { minPasswordLength } from "utils/validations";

const { createPassword } = authApi;

export type SetAuthPasswordSubmitData = {
  password: string;
  token: string | null;
};

type SetPasswordSubmitData = {
  password: string;
  password_confirmation: string;
};

const SetPasswordView: React.FC = () => {
  const { t } = useTranslation();
  const { refreshSession } = useContext(SessionContext);
  const navigate = useNavigate();
  const [isError, setError] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const { handleSubmit, control, formState, watch } = useForm<SetPasswordSubmitData>({ mode: "onChange" });
  const { isValid, errors } = formState;
  const passwordConfirmationError = t("errors.passwords_do_not_match") as string;

  const saveCookieUserReturnUrl = () => {
    cookie.save(USER_RETURN_AHPLUS_COOKIE, "1", config.cookieOptions);
  };

  const onSubmit = async (data: SetPasswordSubmitData) => {
    setError(false);
    setIsLoading(true);

    const token = new URLSearchParams(location.search).get("token");

    const authData: SetAuthPasswordSubmitData = { password: data.password, token: token };
    const [error] = await createPassword(authData);

    if (error) {
      setError(true);
      setIsLoading(false);
    } else {
      saveCookieUserReturnUrl();
      refreshSession();
      navigate(generatePath(PAGE_PATHS.HOME_PAGE));
    }
  };

  return (
    <>
      <Heading variant="h4" mb={4} textAlign="left">
        {t("authentication.password_form.header")}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        <VStack spacing={4} mb={10}>
          <Controller
            render={({ field: { onChange, name } }) => (
              <Input
                isInvalid={!!errors.password}
                errorMessage={errors.password?.message || ""}
                label={t("authentication.password")}
                size="lg"
                name={name}
                id="password"
                type="password"
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                autoComplete="new-password"
                data-testid="password"
              />
            )}
            name="password"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              minLength: {
                value: minPasswordLength,
                message: t("errors.too_short", { min: minPasswordLength, attribute: t("authentication.password") }),
              },
            }}
            defaultValue=""
          />

          <Controller
            render={({ field: { onChange, name } }) => (
              <Input
                isInvalid={!!errors.password_confirmation}
                errorMessage={errors.password_confirmation?.message || ""}
                label={t("authentication.password_confirmation")}
                size="lg"
                name={name}
                type="password"
                onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
                autoComplete="new-password"
                data-testid="password-confirmation"
              />
            )}
            name="password_confirmation"
            control={control}
            rules={{
              required: { value: true, message: t("errors.required") },
              validate: (value) => value === watch("password") || passwordConfirmationError,
            }}
            defaultValue=""
          />

          {isError && (
            <Box w="100%">
              <ErrorBadge>
                <Trans i18nKey="authentication.expired_link">
                  The link you are trying to use has already expired. If you want to set a new password go
                  <Link href={config.webappResetPasswordUrl} onClick={saveCookieUserReturnUrl}>
                    here
                  </Link>
                  . A new link will be valid 60 minutes from receiving an email.
                </Trans>
              </ErrorBadge>
            </Box>
          )}
        </VStack>

        <Button size="s" width="100%" onClick={handleSubmit(onSubmit)} isDisabled={!isValid || isLoading}>
          {t("authentication.password_form.submit")}
        </Button>
      </form>
    </>
  );
};

export default SetPasswordView;
